// components/PropertyIncomeStep.tsx
import React, { useState } from 'react';
import { Control, Controller, FieldErrors, useWatch } from 'react-hook-form';
import { Row, Col, Typography, Divider, Spin, Button, Input } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { ErrorText } from '@/components/Elements';
import RadioGroupField from '@/components/Form/RadioGroupField';
import { useGetPersonalPropertyListQuery } from '@/pages/mortgage/apis/propertyAPI';
import TaxPropertyCard from '@/pages/tax/components/TaxPropertyCard';
import AddPropertyModal from './AddPropertyModal';
import { PreLodgementChecklistFormValues } from '../types/preLodgementChecklist';

const { Title, Text } = Typography;
const { TextArea } = Input;

const YES_NO = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

type Props = {
  control: Control<PreLodgementChecklistFormValues>;
  error: FieldErrors<PreLodgementChecklistFormValues>;
};

export const PropertyIncomeStep: React.FC<Props> = ({ control, error }) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const disposalAnswer = useWatch({ control, name: 'propertyDisposalAnswer' });
  const refinanceAnswer = useWatch({
    control,
    name: 'propertyRefinanceAnswer',
  });
  const purchaseAnswer = useWatch({ control, name: 'propertyPurchaseAnswer' });

  const { data: propertyData, isLoading } = useGetPersonalPropertyListQuery({
    property_location: 'australian',
  });

  const properties = propertyData?.data ?? [];

  return (
    <div>
      <Title level={5} style={{ marginBottom: 4, marginTop: 0 }}>
        Property Income
      </Title>
      <Text type="secondary">
        Australian properties and any changes this financial year.
      </Text>

      <Divider style={{ margin: '12px 0 20px' }} />

      {isLoading && <Spin />}

      {!isLoading && properties.length === 0 && (
        <Text type="secondary">
          No Australian properties found. Add one below.
        </Text>
      )}

      {!isLoading &&
        properties.map((property: any) => (
          <TaxPropertyCard key={property.id} property={property} />
        ))}

      <Button
        type="primary"
        ghost
        block
        size="large"
        icon={<PlusOutlined />}
        style={{ marginTop: 16 }}
        onClick={() => setIsAddModalOpen(true)}
      >
        Add another property
      </Button>

      <AddPropertyModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
      />

      <Divider />

      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <RadioGroupField
            label="Any property disposals?"
            name="propertyDisposalAnswer"
            control={control}
            options={YES_NO}
            error={error?.propertyDisposalAnswer?.message}
            cols={{ sm: 2, md: 2, lg: 2 }}
          />
          {disposalAnswer === true && (
            <div style={{ marginTop: 12 }}>
              <Controller
                name="propertyDisposalNotes"
                control={control}
                render={({ field }) => (
                  <TextArea
                    {...field}
                    placeholder="Which properties and what happened — sale price, settlement date…"
                    status={error?.propertyDisposalNotes ? 'error' : undefined}
                    autoSize={{ minRows: 3, maxRows: 6 }}
                    styles={{ textarea: { lineHeight: 1.6 } }}
                  />
                )}
              />
              {error?.propertyDisposalNotes?.message && (
                <ErrorText error={error.propertyDisposalNotes.message} />
              )}
            </div>
          )}
        </Col>

        <Col xs={24}>
          <RadioGroupField
            label="Did you refinance any properties this financial year?"
            name="propertyRefinanceAnswer"
            control={control}
            options={YES_NO}
            error={error?.propertyRefinanceAnswer?.message}
            cols={{ sm: 2, md: 2, lg: 2 }}
          />
          {refinanceAnswer === true && (
            <div style={{ marginTop: 12 }}>
              <Controller
                name="propertyRefinanceNotes"
                control={control}
                render={({ field }) => (
                  <TextArea
                    {...field}
                    placeholder="Which properties and which lenders — old lender, new lender, date…"
                    status={error?.propertyRefinanceNotes ? 'error' : undefined}
                    autoSize={{ minRows: 3, maxRows: 6 }}
                    styles={{ textarea: { lineHeight: 1.6 } }}
                  />
                )}
              />
              {error?.propertyRefinanceNotes?.message && (
                <ErrorText error={error.propertyRefinanceNotes.message} />
              )}
            </div>
          )}
        </Col>

        <Col xs={24}>
          <RadioGroupField
            label="Did you buy any new properties this financial year?"
            name="propertyPurchaseAnswer"
            control={control}
            options={YES_NO}
            error={error?.propertyPurchaseAnswer?.message}
            cols={{ sm: 2, md: 2, lg: 2 }}
          />
          {purchaseAnswer === true && (
            <div style={{ marginTop: 12 }}>
              <Controller
                name="propertyPurchaseNotes"
                control={control}
                render={({ field }) => (
                  <TextArea
                    {...field}
                    placeholder="Address, settlement date, purchase price…"
                    status={error?.propertyPurchaseNotes ? 'error' : undefined}
                    autoSize={{ minRows: 3, maxRows: 6 }}
                    styles={{ textarea: { lineHeight: 1.6 } }}
                  />
                )}
              />
              {error?.propertyPurchaseNotes?.message && (
                <ErrorText error={error.propertyPurchaseNotes.message} />
              )}
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
};
